/**
 * No Team Access Page
 *
 * Displayed when a user is authenticated but not assigned to any team.
 * They need to be invited to a team before they can access the app.
 */
import { Box, Button, Typography } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { useAuthContext } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

// Translation constants
const TEAM_ACCESS_REQUIRED = gettext("Team Access Required");
const NEED_TEAM_ASSIGNMENT = gettext(
  "You need to be assigned to a team before you can access Captured Knowledge."
);
const CONTACT_ADMIN = gettext(
  "Please contact your team administrator or reach out to our support team for assistance:"
);
const SIGN_OUT = gettext("Sign Out");

export function NoTeamAccessPage() {
  const { supportEmail } = useAuthContext() as ReturnType<
    typeof useAuthContext
  > & {
    supportEmail?: string;
  };

  const email = supportEmail || "support@capturedknowledge.ai";

  return (
    <AuthLayout title={TEAM_ACCESS_REQUIRED}>
      <Box
        sx={{
          padding: "0 2.5rem 1.5rem",
          backgroundColor: "white",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.95rem",
            color: BRAND_COLORS.text.secondary,
            lineHeight: 1.6,
            marginBottom: "0.75rem",
          }}
        >
          {NEED_TEAM_ASSIGNMENT}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.95rem",
            color: BRAND_COLORS.text.secondary,
            lineHeight: 1.6,
            marginBottom: "0.75rem",
          }}
        >
          {CONTACT_ADMIN}
        </Typography>

        <Typography
          sx={{
            fontSize: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <Box
            component="a"
            href={`mailto:${email}`}
            sx={{
              color: BRAND_COLORS.link,
              textDecoration: "underline",
              userSelect: "text",
              "&:hover": {
                color: BRAND_COLORS.linkHover,
              },
            }}
          >
            {email}
          </Box>
        </Typography>

        <Box
          sx={{
            marginTop: "2rem",
            paddingTop: "1.5rem",
            borderTop: `1px solid ${BRAND_COLORS.border}`,
          }}
        >
          <Button
            href="/accounts/logout/"
            variant="text"
            startIcon={<LogoutIcon />}
            sx={{
              color: BRAND_COLORS.link,
              textTransform: "none",
              fontWeight: 500,
              fontFamily: "'Montserrat', sans-serif",
              "&:hover": {
                color: BRAND_COLORS.linkHover,
                backgroundColor: "transparent",
              },
            }}
            data-testid="sign-out-button"
          >
            {SIGN_OUT}
          </Button>
        </Box>
      </Box>
    </AuthLayout>
  );
}
